import React, { useState, useEffect } from 'react';
import { compatAPI } from '../../utils/api';
import useReleaseFilter from '../../hooks/useReleaseFilter';
import ReleaseFilterBar from '../../components/ReleaseFilterBar';

// ErrorLeaderboardPage — flat (default) or grouped view of cast errors.
// Group-by chips toggle between Ungrouped / By Phone / By Protocol /
// By Stage. The Ungrouped view rows are click-to-expand for a
// drill-down (affected phones / protocols / stage breakdown / sample
// messages / recent sessions). Free-text filter substring-matches the
// error_code server-side.

const GROUP_BY_OPTIONS = [
    { id: '',         label: 'Ungrouped (default)' },
    { id: 'phone',    label: 'By Phone' },
    { id: 'protocol', label: 'By Protocol' },
    { id: 'stage',    label: 'By Stage' },
];

export default function ErrorLeaderboardPage() {
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [appVersions, setAppVersions] = useState([]);
    const [currentVersion, setCurrentVersion] = useState(null);
    const [groupBy, setGroupBy] = useState('');
    const [errorCode, setErrorCode] = useState('');
    // Debounced free-text filter — the input fires immediately, but we
    // only refetch after 300ms of typing inactivity so the server isn't
    // hammered.
    const [debouncedCode, setDebouncedCode] = useState('');
    const [expanded, setExpanded] = useState({});  // {error_code: detailObject|null|loading}
    const filter = useReleaseFilter();

    useEffect(() => {
        compatAPI.getBuildsSeen(filter.days).then(r => {
            setAppVersions(r.data?.app_versions || []);
            setCurrentVersion(r.data?.current_app_version_name || null);
        }).catch(() => {});
    }, [filter.days]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedCode(errorCode.trim()), 300);
        return () => clearTimeout(t);
    }, [errorCode]);

    useEffect(() => {
        setLoading(true);
        const params = { ...filter.queryParams };
        if (groupBy)      params.group_by   = groupBy;
        if (debouncedCode) params.error_code = debouncedCode;
        compatAPI.getErrors(params)
            .then(res => {
                const d = res.data;
                const rows = Array.isArray(d) ? d
                           : Array.isArray(d?.errors) ? d.errors
                           : Array.isArray(d?.data)   ? d.data
                           : [];
                setErrors(rows);
                // Collapse all on filter change so stale detail rows
                // don't survive into the new dataset.
                setExpanded({});
            })
            .catch(err => console.error('Errors error:', err))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filter.queryParams.release_op,
        filter.queryParams.release_value,
        filter.queryParams.days,
        filter.queryParams.layer,
        groupBy,
        debouncedCode,
    ]);

    const toggleRow = async (row) => {
        const key = row.error_code + '|' + (row.error_stage || '');
        if (expanded[key]) {
            setExpanded({ ...expanded, [key]: undefined });
            return;
        }
        setExpanded({ ...expanded, [key]: { loading: true } });
        try {
            const params = { ...filter.queryParams, error_code: row.error_code };
            if (row.error_stage) params.error_stage = row.error_stage;
            const res = await compatAPI.getErrorDetail(params);
            setExpanded(prev => ({ ...prev, [key]: res.data }));
        } catch (err) {
            console.error('error_detail fetch failed:', err);
            setExpanded(prev => ({ ...prev, [key]: { error: true } }));
        }
    };

    return (
        <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Leaderboard</h2>
            <ReleaseFilterBar filter={filter} appVersions={appVersions} currentVersion={currentVersion} />

            {/* Group-by chips + free-text filter */}
            <div className="bg-white rounded-lg shadow-md p-3 mb-4 flex items-center gap-2 flex-wrap text-sm">
                <span className="text-xs uppercase text-gray-500 mr-1">Group by</span>
                {GROUP_BY_OPTIONS.map(o => (
                    <span
                        key={o.id || 'ungrouped'}
                        onClick={() => setGroupBy(o.id)}
                        className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition ${
                            groupBy === o.id ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}>
                        {o.label}
                    </span>
                ))}
                <span className="text-xs uppercase text-gray-500 ml-4 mr-1">Filter</span>
                <input
                    value={errorCode}
                    onChange={e => setErrorCode(e.target.value)}
                    placeholder="error_code substring…"
                    className="border rounded px-2 py-1 text-xs font-mono w-64"
                />
                {errorCode && (
                    <button
                        onClick={() => setErrorCode('')}
                        className="text-xs text-gray-500 hover:text-gray-700">clear</button>
                )}
            </div>

            {loading && <div className="text-gray-400 mb-2">Loading…</div>}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="overflow-x-auto">
                    {groupBy === '' && (
                        <UngroupedTable
                            errors={errors}
                            expanded={expanded}
                            onToggle={toggleRow} />
                    )}
                    {groupBy === 'phone'    && <PhoneTable    rows={errors} />}
                    {groupBy === 'protocol' && <ProtocolTable rows={errors} />}
                    {groupBy === 'stage'    && <StageTable    rows={errors} />}
                </div>
            </div>
        </div>
    );
}

// ── Ungrouped (default) ───────────────────────────────────────────────
function UngroupedTable({ errors, expanded, onToggle }) {
    return (
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
                <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-6"></th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Occurrences</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unique Devices</th>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {errors.map((err, idx) => {
                    const key = err.error_code + '|' + (err.error_stage || '');
                    const detail = expanded[key];
                    const isOpen = !!detail;
                    return (
                        <React.Fragment key={idx}>
                            <tr
                                onClick={() => onToggle(err)}
                                className="hover:bg-gray-50 cursor-pointer">
                                <td className="px-4 py-3 text-xs text-gray-400 font-mono">{isOpen ? 'v' : '>'}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{err.error_domain || err.domain || '-'}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">{err.error_code || '-'}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-xs font-mono text-gray-600">{err.error_stage || err.stage || '-'}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">{err.occurrences ?? err.count ?? 0}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{err.unique_devices ?? err.affected_devices ?? 0}</td>
                            </tr>
                            {isOpen && (
                                <tr>
                                    <td colSpan="6" className="px-4 py-4 bg-gray-50 border-t border-gray-200">
                                        <ErrorDetailPanel detail={detail} />
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
                {errors.length === 0 && (
                    <tr><td colSpan="6" className="px-4 py-6 text-sm text-gray-400 text-center">No errors recorded</td></tr>
                )}
            </tbody>
        </table>
    );
}

function ErrorDetailPanel({ detail }) {
    if (!detail) return null;
    if (detail.loading) return <div className="text-xs text-gray-400">Loading drill-down…</div>;
    if (detail.error)   return <div className="text-xs text-red-500">Failed to load drill-down.</div>;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <MiniTable
                title="Affected phones"
                cols={[
                    { key: 'phone_manufacturer', label: 'Manuf.' },
                    { key: 'phone_model',        label: 'Model' },
                    { key: 'soc_model',          label: 'SoC' },
                    { key: 'occurrences',        label: 'Count' },
                ]}
                rows={detail.phones} />
            <MiniTable
                title="Affected protocols"
                cols={[
                    { key: 'protocol',    label: 'Protocol' },
                    { key: 'occurrences', label: 'Count' },
                ]}
                rows={detail.protocols} />
            {detail.stages && detail.stages.length > 1 && (
                <MiniTable
                    title="Stage breakdown (this error_code)"
                    cols={[
                        { key: 'error_stage', label: 'Stage' },
                        { key: 'occurrences', label: 'Count' },
                    ]}
                    rows={detail.stages} />
            )}
            <div>
                <div className="font-semibold mb-1 text-gray-700 uppercase">Sample messages</div>
                {(!detail.messages || detail.messages.length === 0) ? (
                    <div className="text-gray-400">No error_detail strings recorded.</div>
                ) : (
                    <ul className="space-y-1">
                        {detail.messages.map((m, i) => (
                            <li key={i} className="font-mono text-gray-700 break-all">
                                <span className="text-gray-400 mr-1">{m.occurrences}×</span>
                                {m.error_detail}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div className="md:col-span-2">
                <div className="font-semibold mb-1 text-gray-700 uppercase">Recent sessions</div>
                {(!detail.recent_sessions || detail.recent_sessions.length === 0) ? (
                    <div className="text-gray-400">No recent sessions recorded.</div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                        <thead className="bg-white">
                            <tr>
                                <th className="px-2 py-1 text-left">Time</th>
                                <th className="px-2 py-1 text-left">Protocol</th>
                                <th className="px-2 py-1 text-left">Sink</th>
                                <th className="px-2 py-1 text-left">Duration</th>
                                <th className="px-2 py-1 text-left">Session</th>
                            </tr>
                        </thead>
                        <tbody>
                            {detail.recent_sessions.map((s, i) => (
                                <tr key={i} className="hover:bg-white">
                                    <td className="px-2 py-1 font-mono">
                                        {s.timestamp_start ? new Date(s.timestamp_start).toLocaleString() : '-'}
                                    </td>
                                    <td className="px-2 py-1">{s.protocol || '-'}</td>
                                    <td className="px-2 py-1">{s.sink_name || '-'}</td>
                                    <td className="px-2 py-1 font-mono">{s.total_play_time_s != null ? s.total_play_time_s + 's' : '-'}</td>
                                    <td className="px-2 py-1 font-mono">
                                        <a href={`/admin/session/${s.session_id}`} className="text-blue-600 hover:underline">
                                            {(s.session_id || '').slice(0, 8)}…
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function MiniTable({ title, cols, rows }) {
    return (
        <div>
            <div className="font-semibold mb-1 text-gray-700 uppercase">{title}</div>
            {(!rows || rows.length === 0) ? (
                <div className="text-gray-400">No data.</div>
            ) : (
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white">
                        <tr>
                            {cols.map(c => (
                                <th key={c.key} className="px-2 py-1 text-left text-gray-600">{c.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>
                                {cols.map(c => (
                                    <td key={c.key} className="px-2 py-1 font-mono text-gray-700">{r[c.key] ?? '-'}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ── Grouped — phone / protocol / stage ────────────────────────────────
function TopCodesCell({ codes }) {
    if (!codes || codes.length === 0) return <span className="text-gray-400">-</span>;
    return (
        <span className="font-mono text-xs">
            {codes.slice(0, 3).map((c, i) => (
                <span key={i} className="inline-block mr-2">
                    {c.error_code}<span className="text-gray-400 ml-0.5">·{c.occurrences}</span>
                </span>
            ))}
        </span>
    );
}

function PhoneTable({ rows }) {
    return (
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
                <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Manuf.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SoC</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Android</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Occ.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Distinct codes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Top 3 codes</th>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm">{r.phone_manufacturer}</td>
                        <td className="px-4 py-2 text-sm font-mono">{r.phone_model}</td>
                        <td className="px-4 py-2 text-xs text-gray-700">{r.soc_model}</td>
                        <td className="px-4 py-2 text-xs">{r.android_version}</td>
                        <td className="px-4 py-2 text-sm font-semibold">{r.occurrences}</td>
                        <td className="px-4 py-2 text-sm">{r.distinct_codes}</td>
                        <td className="px-4 py-2"><TopCodesCell codes={r.top_codes} /></td>
                    </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan="7" className="px-4 py-6 text-sm text-gray-400 text-center">No errors</td></tr>}
            </tbody>
        </table>
    );
}

function ProtocolTable({ rows }) {
    return (
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
                <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Protocol</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Occ.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Distinct codes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Top 3 codes</th>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm font-mono">{r.protocol}</td>
                        <td className="px-4 py-2 text-sm font-semibold">{r.occurrences}</td>
                        <td className="px-4 py-2 text-sm">{r.distinct_codes}</td>
                        <td className="px-4 py-2"><TopCodesCell codes={r.top_codes} /></td>
                    </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan="4" className="px-4 py-6 text-sm text-gray-400 text-center">No errors</td></tr>}
            </tbody>
        </table>
    );
}

function StageTable({ rows }) {
    return (
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
                <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Occ.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Distinct codes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Top 3 codes</th>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm font-mono">{r.error_stage}</td>
                        <td className="px-4 py-2 text-sm font-semibold">{r.occurrences}</td>
                        <td className="px-4 py-2 text-sm">{r.distinct_codes}</td>
                        <td className="px-4 py-2"><TopCodesCell codes={r.top_codes} /></td>
                    </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan="4" className="px-4 py-6 text-sm text-gray-400 text-center">No errors</td></tr>}
            </tbody>
        </table>
    );
}
